'use client'

import { isEqual } from 'lodash'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Input from '@/components/atomic/input'
import { BodyXS } from '@/components/atomic/text'

import { Slider as BaseSlider } from './base'
import { isValidDecimalInput } from './helpers'

type BaseSliderProps = React.ComponentProps<typeof BaseSlider>

export interface SliderProps extends BaseSliderProps {
  label?: string
  showInput?: boolean
  showMinMaxInputs?: boolean
  disableMinMaxInputs?: boolean
  showRangeDisplay?: boolean
  rangeDisplay?: React.ReactNode
  rangeInputClassName?: string
  formatter?: (value: number) => string
  parser?: (input: string) => number
  defaultValue: number[]
  onValueChange?: (value: number[]) => void
  onSliderDrag?: (value: number[]) => void
  min: number
  max: number
  hidePlus?: boolean
  large?: boolean
}

const INPUT_DEBOUNCE_MS = 400

/**
 * @deprecated Use `Slider` from `@moment/design-system-ui/slider` instead.
 */
// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function Slider({
  label,
  showInput = false,
  showMinMaxInputs = false,
  disableMinMaxInputs = false,
  showRangeDisplay = false,
  rangeDisplay,
  rangeInputClassName,
  formatter,
  parser,
  onValueChange,
  onSliderDrag,
  hidePlus = false,
  large = false,
  ...props
}: SliderProps) {
  // Tracks the current value of the slider after the user's interaction is complete
  const [stableValue, setStableValue] = useState<number[]>(props.defaultValue)
  const stableValueRef = useRef<number[]>(props.defaultValue)
  // Suppress '+' suffix on blur only when the user actually typed into the input.
  const editedIndexRef = useRef<number | null>(null)
  // Tracks the current value of the slider during the user's interaction with it
  const [unstableValue, setUnstableValue] = useState<number[]>(
    props.defaultValue,
  )

  const formatPlainValue = useCallback(
    (value: string): string => {
      const numericalValue = Number(value)
      return formatter ? formatter(numericalValue) : value
    },
    [formatter],
  )

  const formatValue = useCallback(
    (value: string): string => {
      const formattedValue = formatPlainValue(value)
      if (Number(value) === props.max && !hidePlus) {
        return `${formattedValue}+`
      }
      return formattedValue
    },
    [formatPlainValue, props.max, hidePlus],
  )

  // Tracks the formatted value of the slider for display in the input
  const [formattedValue, setFormattedValue] = useState<string[]>(
    props.defaultValue.map((value) => formatValue(value.toString())),
  )

  //
  // Intermediate change callbacks
  //

  const clampValue = useCallback(
    (values: number[]): number[] => {
      if (values.length === 1) {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        return [Math.max(props.min, Math.min(props.max, values[0]!))]
      }
      return [
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        Math.max(props.min, Math.min(values[0]!, values[1]!)),
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        Math.min(props.max, Math.max(values[0]!, values[1]!)),
      ]
    },
    [props.min, props.max],
  )

  // Commits the typed value to stableValue (and therefore onValueChange) after
  // a short idle period, so changes propagate to the parent without requiring
  // the user to click away first. Commit does not touch unstableValue or
  // formattedValue, so the user's in-progress input is never clobbered.
  const debouncedCommit = useMemo(
    () =>
      debounce((values: number[]) => {
        const clamped = clampValue(values)
        if (!isEqual(clamped, stableValueRef.current)) {
          setStableValue(clamped)
        }
      }, INPUT_DEBOUNCE_MS),
    [clampValue],
  )

  useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit])

  const handleSliderChange = useCallback(
    (value: number[]) => {
      // Update the unstable value
      setUnstableValue(value)
      // Update and format the value in the input so it changes in real-time
      setFormattedValue(value.map((value) => formatValue(value.toString())))
      onSliderDrag?.(value)
    },
    [formatValue, onSliderDrag],
  )

  const handleInputChange = useCallback(
    (value: string, index: number) => {
      editedIndexRef.current = index
      const isSingleValue = unstableValue.length === 1

      if (value === '') {
        // If the input is empty, set the value to min or max
        // accordingly, but show the empty string in the input
        setFormattedValue(
          unstableValue.map((value, i) =>
            i === index ? '' : formatValue(value.toString()),
          ),
        )
        setUnstableValue(
          unstableValue.map((value, i) =>
            // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
            i === index ? (index === 0 ? props.min : props.max) : value,
          ),
        )
        return
      }

      // Check if the input is a valid intermediate or final decimal number
      // Skip validation if parser is provided (parser will handle validation)
      if (!parser && !isValidDecimalInput(value)) {
        // Reject invalid input (like multiple decimal points, letters, etc.)
        return
      }

      // Special handling for decimal point only - treat as 0 for calculations but preserve display
      const displayValue = value
      const calculationValue = parser
        ? parser(value)
        : // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
          // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
          isNaN(Number(value))
          ? 0
          : Number(value)

      // biome-ignore lint/suspicious/noImplicitAnyLet: Bulk suppression as part of biome migration
      let updatedUnstableValue
      // biome-ignore lint/suspicious/noImplicitAnyLet: Bulk suppression as part of biome migration
      let updatedFormattedValue

      if (isSingleValue) {
        // Single value slider - only update the one value
        updatedUnstableValue = [calculationValue]
        updatedFormattedValue = [displayValue]
      } else if (index === 0) {
        // Range slider - update min value
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        updatedUnstableValue = [calculationValue, unstableValue[1]!]
        updatedFormattedValue = [
          // While input is changing, show the raw input value to preserve decimal points
          displayValue,
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          formatValue(unstableValue[1]!.toString()),
        ]
      } else {
        // Range slider - update max value
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        updatedUnstableValue = [unstableValue[0]!, calculationValue]
        updatedFormattedValue = [
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          formatValue(unstableValue[0]!.toString()),
          // While input is changing, show the raw input value to preserve decimal points
          displayValue,
        ]
      }
      setUnstableValue(updatedUnstableValue)
      setFormattedValue(updatedFormattedValue)
      debouncedCommit(updatedUnstableValue)
    },
    [unstableValue, formatValue, props.min, props.max, parser, debouncedCommit],
  )

  const handleInputFocus = useCallback(
    (index: number) => {
      // While input is focused, show the unformatted value to prevent
      // the user from needing to interact with symbols
      // UNLESS a parser is provided - then keep formatted value
      if (parser) {
        // Keep the formatted value when parser is present
        return
      }
      setFormattedValue(
        stableValue.map((value, i) =>
          i === index ? value.toString() : formatValue(value.toString()),
        ),
      )
    },
    [formatValue, stableValue, parser],
  )

  const handleInputBlur = useCallback(() => {
    const editedIndex = editedIndexRef.current
    editedIndexRef.current = null
    debouncedCommit.cancel()
    // If the user cleared an input, restore that entry to the last committed
    // (stable) value on blur — don't treat an empty input as a commit to the
    // slider's min/max bound.
    const restored = unstableValue.map((v, i) =>
      formattedValue[i] === '' ? (stableValueRef.current[i] ?? v) : v,
    )
    const clamped = clampValue(restored)
    if (!isEqual(clamped, stableValueRef.current)) {
      setStableValue(clamped)
    }
    setUnstableValue(clamped)
    setFormattedValue(
      clamped.map((v, i) => {
        const str = v.toString()
        return i === editedIndex ? formatPlainValue(str) : formatValue(str)
      }),
    )
  }, [
    clampValue,
    unstableValue,
    formattedValue,
    formatValue,
    formatPlainValue,
    debouncedCommit,
  ])

  //
  // End change callbacks

  const handleSliderChangeEnd = useCallback(() => {
    setStableValue(unstableValue)
    setFormattedValue(
      unstableValue.map((value) => formatValue(value.toString())),
    )
  }, [unstableValue, formatValue])

  // Want handler to always have the latest value for onValueChange
  // (e.g., if it is memoized using query params) but only want to call it
  // when the stable value changes
  const handleValueChange = useCallback(
    (value: number[]) => {
      onValueChange?.(value)
    },
    [onValueChange],
  )

  const [stableValue0, stableValue1] = stableValue
  useEffect(() => {
    if (
      stableValue0 === stableValueRef.current[0] &&
      stableValue1 === stableValueRef.current[1]
    ) {
      return
    }
    handleValueChange(stableValue)
    stableValueRef.current = [...stableValue]
  }, [stableValue0, stableValue1, stableValue, handleValueChange])

  return (
    <div>
      {label && (
        <div className={showInput ? 'mb-0' : 'mb-[12px]'}>
          <BodyXS color="text-secondary">{label}</BodyXS>
        </div>
      )}
      <div className="flex items-center gap-4">
        <BaseSlider
          data-testid="base-slider"
          className="w-full"
          onValueChange={handleSliderChange}
          onLostPointerCapture={handleSliderChangeEnd}
          value={unstableValue}
          disabled={props.disabled}
          {...props}
        />
        {showInput && (
          <div className="max-w-[48px]">
            <Input
              width="full"
              size="small"
              placeholder={formatValue(props.min.toString())}
              value={formattedValue[0]}
              onChange={(e) => {
                handleInputChange(e.target.value, 0)
              }}
              onFocus={() => handleInputFocus(0)}
              onBlur={handleInputBlur}
              disabled={props.disabled}
            />
          </div>
        )}
      </div>
      {showMinMaxInputs && props.defaultValue.length > 1 && (
        <div className={`flex justify-between mt-3 ${rangeInputClassName}`}>
          <div className={`max-w-[${large ? '160px' : '110px'}]`}>
            <Input
              width="full"
              size="small"
              placeholder={formatValue(props.min.toString())}
              value={formattedValue[0]}
              slotProps={{
                side: 'left',
                label: 'Min',
              }}
              onChange={(e) => {
                handleInputChange(e.target.value, 0)
              }}
              onFocus={() => handleInputFocus(0)}
              onBlur={handleInputBlur}
              disabled={props.disabled || disableMinMaxInputs}
            />
          </div>
          {showRangeDisplay && (
            <div className="flex-1 flex justify-center">{rangeDisplay}</div>
          )}
          <div className={`max-w-[${large ? '160px' : '110px'}]`}>
            <Input
              width="full"
              size="small"
              placeholder={formatValue(props.max.toString())}
              value={formattedValue[1]}
              slotProps={{
                side: 'left',
                label: 'Max',
              }}
              onChange={(e) => {
                handleInputChange(e.target.value, 1)
              }}
              onFocus={() => handleInputFocus(1)}
              onBlur={handleInputBlur}
              disabled={props.disabled || disableMinMaxInputs}
            />
          </div>
        </div>
      )}
    </div>
  )
}
